import { useState } from "react";
import toast from "react-hot-toast";

import {
  getAllCourses,
  getCourseById,
  createCourse,
  updateCourse,
  deleteCourse,
  createModule,
  updateModule,
  deleteModule,
} from "../../../Services/trainingService";

import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { LMS_TOAST_MESSAGES } from "../constants";

const DEFAULT_PAGINATION = {
  count: 0,
  offset: 0,
  limit: 10,
};

export const useCourses = () => {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  /* =======================
     FETCH COURSES (WITH PAGINATION)
  ======================= */

  const fetchCourses = async (params = {}, isInitial = false) => {
    setLoading(true);
    try {
      const response = await getAllCourses(params);
      if (response?.status === 200) {
        const results = response.data?.results ?? [];
        const count = response.data?.count ?? results.length;

        setCourses((prev) => (isInitial ? results : [...prev, ...results]));

        setPagination((prev) => ({
          ...prev,
          count,
          offset: isInitial ? results.length : prev.offset + results.length,
        }));

        return results;
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || LMS_TOAST_MESSAGES.COURSE_FETCH_FAILED,
      );
    } finally {
      setLoading(false);
    }
    return [];
  };

  const loadMoreCourses = () => {
    if (pagination.offset >= pagination.count) return;

    fetchCourses(
      {
        offset: pagination.offset,
        limit: pagination.limit,
      },
      false,
    );
  };

  const resetCourses = () => {
    setCourses([]);
    setPagination(DEFAULT_PAGINATION);
  };

  /* =======================
     OTHER APIS (UNCHANGED)
  ======================= */

  const fetchCourseById = async (courseId) => {
    setLoading(true);
    try {
      const response = await getCourseById(courseId);
      if (response?.status === 200) {
        return response.data;
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) ||
          LMS_TOAST_MESSAGES.COURSE_DETAILS_FETCH_FAILED
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const submitCourse = async (payload) => {
    setLoading(true);
    try {
      const res = await createCourse(payload);
      return res.data;
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || LMS_TOAST_MESSAGES.CREATE_COURSE_FAILURE
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editCourse = async (courseId, payload) => {
    setLoading(true);
    try {
      const res = await updateCourse(courseId, payload);
      return res.data;
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || LMS_TOAST_MESSAGES.UPDATE_COURSE_FAILURE
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeCourse = async (courseId) => {
    setLoading(true);
    try {
      const response = await deleteCourse(courseId);
      if (response.status === 204) {
        toast.success("Course deleted successfully");
        setCourses((prev) => prev.filter((c) => c.id !== courseId));
        return true;
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
    return false;
  };

  /* =======================
     Module APIs (Nested)
  ======================= */

  const submitModule = async (courseId, payload) => {
    setLoading(true);
    try {
      const res = await createModule(courseId, payload);
      return res.data;
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || LMS_TOAST_MESSAGES.CREATE_MODULE_FAILURE
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editModule = async (courseId, moduleId, payload) => {
    setLoading(true);
    try {
      const res = await updateModule(courseId, moduleId, payload);
      return res.data;
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || LMS_TOAST_MESSAGES.UPDATE_MODULE_FAILURE
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeModule = async (courseId, moduleId) => {
    setLoading(true);
    try {
      await deleteModule(courseId, moduleId);
      return true;
    } catch (err) {
      toast.error(extractErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    courses,
    pagination,

    /* fetch */
    fetchCourses,
    loadMoreCourses,
    resetCourses,
    fetchCourseById,

    /* course */
    submitCourse,
    editCourse,
    removeCourse,

    /* module */
    submitModule,
    editModule,
    removeModule,
  };
};
